const Sentry = require('@sentry/browser');
const { compressToEncodedURIComponent } = require('lz-string');
const Log = require('./logging.js');
const Markdown = require('./pagedown/Markdown.Sanitizer.js');
const Analytics = require('./analytics.js');
const { getCurrentTab } = require('./powerup-tabs.js');

const converter = Markdown.getSanitizingConverter();

const sanitize = function(text) {
  return String(text).replace(/[&<>"'/]/g, function(s) {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
    }[s];
  });
};
window.sanitize = sanitize;

const markdownToHtml = function(text) {
  const parser = new DOMParser();
  return converter.makeHtml(parser.parseFromString(text, 'text/html').body.textContent || '');
};
window.markdownToHtml = markdownToHtml;

const replaceSlashInSentryTag = tagKey => {
  // Sentry doesn't seem to like forward slashes in tag keys.
  // This replaces all instances of them with dashes.
  return tagKey.replace(/\//g, '-');
};

const handleError = function(error, context) {
  if (/Invalid context, missing board/.test(error.message)) {
  } else if (/Plugin disabled on board/.test(error.message)) {
    console.log('[Butler] Power-Up disabled on board.');
  } else {
    Analytics.getTaskSessions()
      .then(taskSessions => {
        const taskPrefix = 'task_';
        const tags = [];
        Object.keys(taskSessions).forEach(task => {
          const sentryKey = replaceSlashInSentryTag(`${taskPrefix}${task}`);
          tags[sentryKey] = taskSessions[task];
          Sentry.setTag(sentryKey, taskSessions[task]);
        });
      })
      .finally(() => {
        if (error) {
          Sentry.captureException(error);
          Log.logError(error, context);
        } else if (context) {
          Sentry.captureMessage(context);
        }
      });
  }
};

const makeSlug = function(s, sep) {
  let slug;
  if (sep == null) {
    sep = '-';
  }
  if (s) {
    slug = removeAccents(s.toLowerCase())
      .replace(/[^a-z0-9]+/gi, sep)
      .replace(new RegExp(`^${sep}|${sep}$`, 'g'), '');

    if (slug.length > 128) {
      slug = slug.substr(0, 128);
    }
  }

  return slug || sep;
};

const removeAccents = function(s) {
  // NOTE: If this starts to get too complicated, we might consider using
  // a unicode normalization library
  return s
    .replace(/[àáâãäåāăą]/g, 'a')
    .replace(/æ/g, 'ae')
    .replace(/[çćĉċč]/g, 'c')
    .replace(/[ďđ]/g, 'd')
    .replace(/[èéêëēĕėęě]/g, 'e')
    .replace(/[ĝğġģ]/g, 'g')
    .replace(/[ĥħ]/g, 'h')
    .replace(/[ìíîïĩīĭįı]/g, 'i')
    .replace(/[ñńņňŉŋ]/g, 'n')
    .replace(/[òóôõöōŏő]/g, 'o')
    .replace(/œ/g, 'oe')
    .replace(/ř/g, 'r')
    .replace(/[śŝşš]/g, 's')
    .replace(/ß/g, 'ss')
    .replace(/[ùúûüũūŭůűų]/g, 'u')
    .replace(/[ýÿ]/g, 'y');
};

function normalizeTabForUrl(tab) {
  switch (tab) {
    case 'rule':
    case 'rules':
      return 'rules';
    case 'card-button':
      return 'card-buttons';
    case 'board-button':
      return 'board-buttons';
    case 'schedule':
    case 'schedules':
    case 'scheduled':
      return 'schedule';
    case 'on-dates':
    case 'due-dates':
    case 'duedates':
    case 'dates':
      return 'on-dates';
    case 'suggestions':
      return 'suggestions';
    case 'connected-apps':
    case 'app-automations':
      return 'connected-apps';
    default:
      return tab;
  }
}

function normalizeType(tab) {
  switch (tab) {
    case 'rule':
    case 'rules':
      return 'rule';
    case 'card-buttons':
      return 'card-button';
    case 'board-buttons':
      return 'board-button';
    case 'schedules':
    case 'schedule':
    case 'scheduled':
      return 'schedule';
    case 'on-dates':
    case 'due-dates':
    case 'duedates':
    case 'dates':
      return 'on-date';
    default:
      return tab;
  }
}

function updateNavigation({ tab, action, commandId, newCommand, newIcon, newLabel } = {}) {
  const t = TrelloPowerUp.iframe({
    targetOrigin: 'https://trello.com',
  });
  const tabUrlComponent = normalizeTabForUrl(tab || getCurrentTab());
  const actionUrlComponent = action ? `/${action}` : '';
  const commandIdUrlComponent = commandId ? `/${commandId}` : '';
  const newCommandParams = new URLSearchParams();
  if (newCommand) {
    newCommandParams.set('c', compressToEncodedURIComponent(newCommand));
    if (newIcon) {
      newCommandParams.set('i', newIcon);
    }
    if (newLabel) {
      newCommandParams.set('l', newLabel);
    }
  }
  const newCommandUrlComponent = newCommand ? `?${newCommandParams.toString()}` : '';

  return t.board('shortLink', 'name').then(res => {
    const boardSlug = makeSlug(res.name);
    const boardUrl = `https://trello.com/b/${res.shortLink}/${boardSlug}/butler/${tabUrlComponent}${actionUrlComponent}${commandIdUrlComponent}${newCommandUrlComponent}`;
    t.navigate({
      url: boardUrl,
      trigger: false,
    });
  });
}

module.exports = {
  sanitize,
  markdownToHtml,
  handleError,
  makeSlug,
  normalizeTabForUrl,
  normalizeType,
  updateNavigation,
};
